import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useEmployeeNotificationsOptional } from '../../contexts/EmployeeNotificationsContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import EntityComments from '../../components/EntityComments'
import { ActionMenu, Breadcrumb, Button, FormGroup, Select, StatusBadge } from '../../components/ui'
import Icon from '../../components/Icon'
import {
  ACT_FILTER_ALL,
  buildStageBudgetModel,
  formatDateShort,
  formatMoney,
  getInitials,
  getBudgetStatusLabel,
  getBudgetStatusVariant,
  getContractStatusVariant,
} from './stage-budget/stageBudgetUtils'
import StageBudgetTable from './stage-budget/StageBudgetTable'
import { reportPdfButtonStyle } from './reportPreviewUi'
import './stage-budget/StageReportPreview.css'

function filenameFromDisposition(contentDisposition, fallback) {
  const header = String(contentDisposition || '')
  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/^["']|["']$/g, ''))
    } catch (_) {
      return utfMatch[1].replace(/^["']|["']$/g, '').trim() || fallback
    }
  }
  const match = header.match(/filename=["']?([^"';]+)["']?/i)
  return match?.[1]?.trim() || fallback
}

function triggerBlobDownload(data, filename, type) {
  const blob = data instanceof Blob ? data : new Blob([data], { type })
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getApprovalRows(reportData) {
  const approval = reportData?.budget_approval || {}
  if (Array.isArray(approval.approvals)) return approval.approvals
  if (Array.isArray(reportData?.approvals)) return reportData.approvals
  return []
}

function getApprovalState(row) {
  const status = String(row?.status || 'pending').toLowerCase().trim()
  if (status === 'approved') return 'approved'
  if (status === 'rejected') return 'rejected'
  return 'waiting'
}

function getApprovalStateLabel(row) {
  const state = getApprovalState(row)
  if (state === 'approved') return 'Согласовал'
  if (state === 'rejected') return 'Отклонил'
  return 'Ожидает'
}

function getApprovalDate(row) {
  return formatDateTime(row?.approved_at || row?.rejected_at || row?.updated_at)
}

function isOwnApprovalRow(row, user) {
  if (!row || !user) return false
  const eid = user.employee_id
  if (eid != null && eid !== '' && row.employee_id != null && String(row.employee_id) === String(eid)) {
    return true
  }
  const name = String(user.employee_full_name || user.full_name || '').trim()
  return Boolean(name) && String(row.employee_name || '').trim() === name
}

function toggleOnEnterOrSpace(event, toggle) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  toggle()
}

function StageReportPreview() {
  const { id: idParam } = useParams()
  const id = idParam ? decodeURIComponent(idParam) : ''
  const encodedId = encodeURIComponent(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const notifications = useEmployeeNotificationsOptional()
  usePageHeader({ title: null, hideTopHeader: true }, [])

  const [reportData, setReportData] = useState(null)
  const [approvalSending, setApprovalSending] = useState(false)
  const [signerLoading, setSignerLoading] = useState(false)
  const [approvalMessage, setApprovalMessage] = useState({ type: '', text: '' })
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectComment, setRejectComment] = useState('')
  const [quarters, setQuarters] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)
  const [periodType, setPeriodType] = useState('all')
  const [selectedQuarter, setSelectedQuarter] = useState(null)
  const [selectedActId, setSelectedActId] = useState(ACT_FILTER_ALL)
  const [passportCollapsed, setPassportCollapsed] = useState(false)
  const [approvalCollapsed, setApprovalCollapsed] = useState(false)

  const buildReportParams = useCallback(() => {
    const params = new URLSearchParams()
    if (periodType === 'quarter' && selectedQuarter) {
      params.set('period', 'quarter')
      params.set('year', String(selectedQuarter.year))
      params.set('quarter', String(selectedQuarter.quarter))
    } else {
      params.set('period', 'all')
    }
    return params.toString()
  }, [periodType, selectedQuarter])

  const fetchReport = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/company/stages/${encodedId}/report?${buildReportParams()}`)
      setReportData(response.data || null)
      setSelectedActId(ACT_FILTER_ALL)
    } catch (err) {
      setError(err.response?.status === 404 ? 'Стадия не найдена' : 'Не удалось загрузить бюджет стадии')
    } finally {
      setLoading(false)
    }
  }, [id, encodedId, buildReportParams])

  const fetchQuarters = useCallback(async () => {
    if (!id) return
    try {
      const response = await api.get(`/company/stages/${encodedId}/report/quarters`)
      setQuarters(response.data?.quarters || [])
    } catch (_) {
      setQuarters([])
    }
  }, [id, encodedId])

  useEffect(() => {
    fetchQuarters()
  }, [fetchQuarters])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  const model = useMemo(() => buildStageBudgetModel(reportData, selectedActId), [reportData, selectedActId])
  const contract = reportData?.contract || {}
  const budget = reportData?.budget || {}
  const stage = model.stage || {}
  const stageType = stage.correction_name || stage.type || stage.distribution_type || id || 'Стадия'
  const contractId = contract.id
  const contractNumber = contract.number || 'Договор'
  const clientName = contract.client_name || '—'
  const objectName = contract.object_name || ''
  const previewBudgetStatus = budget.approval_status || reportData?.budget_approval?.status || contract.budget_status
  const budgetStatusLabel = getBudgetStatusLabel(previewBudgetStatus)
  const budgetStatusVariant = getBudgetStatusVariant(previewBudgetStatus)
  const contractStatusLabel = contract.status
    ? String(contract.status).slice(0, 1).toUpperCase() + String(contract.status).slice(1)
    : ''
  const backUrl = contractId ? `/contract/${contractId}` : `/stage/${encodedId}`
  const approvals = getApprovalRows(reportData)
  const approvedCount = approvals.filter((row) => getApprovalState(row) === 'approved').length
  const rejectedCount = approvals.filter((row) => getApprovalState(row) === 'rejected').length
  const approvalTotal = approvals.length
  const approvalProgress = approvalTotal > 0 ? Math.round((approvedCount / approvalTotal) * 100) : 0
  const distributionRules = reportData?.distribution_rules || {}
  const customRulesCount = toNumber(distributionRules.custom_team_percentages_count)
  const customRulesIssues = Array.isArray(distributionRules.custom_team_percentages_issues)
    ? distributionRules.custom_team_percentages_issues
    : []
  const gipRulesCount = toNumber(distributionRules.gip_rules_count)
  const customRulesQuery = new URLSearchParams()
  if (budget.id) customRulesQuery.set('budget_id', String(budget.id))
  customRulesQuery.set('stage_id', id)
  if (contractId) customRulesQuery.set('contract_id', contractId)
  const customRulesUrl = `/admin/budget/custom-team-percentages?${customRulesQuery.toString()}`
  const createCustomRulesQuery = new URLSearchParams(customRulesQuery)
  createCustomRulesQuery.set('create', '1')
  const createCustomRulesUrl = `/admin/budget/custom-team-percentages?${createCustomRulesQuery.toString()}`
  const primaryCustomRulesIssue = customRulesIssues[0]
  const customRulesIssueLabel = primaryCustomRulesIssue
    ? `${primaryCustomRulesIssue.difference < 0 ? 'Не хватает' : 'Превышение'} ${Math.abs(toNumber(primaryCustomRulesIssue.difference)).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`
    : ''
  const gipRulesQuery = new URLSearchParams()
  if (budget.id) gipRulesQuery.set('budget_id', String(budget.id))
  gipRulesQuery.set('stage_id', id)
  const gipRulesUrl = `/admin/budget/gip-rules?${gipRulesQuery.toString()}`
  const createGipRulesQuery = new URLSearchParams(gipRulesQuery)
  createGipRulesQuery.set('create', '1')
  const createGipRulesUrl = `/admin/budget/gip-rules?${createGipRulesQuery.toString()}`
  const plannedDate = stage.planned_date || stage.completion_date || stage.deadline || stage.fact_date
  const approvalLink = budget.id ? `/company/budgets/${budget.id}/approvals` : null
  const editBudgetContractId = budget.contract_id || contractId
  const editBudgetStageId = id || budget.stage_id || stage.id
  const editBudgetUrl = editBudgetContractId && editBudgetStageId
    ? (stage.correction_name ? `/company/corrections/${encodeURIComponent(editBudgetStageId)}/edit` : `/company/contracts/${editBudgetContractId}/stages/${encodeURIComponent(editBudgetStageId)}/edit`)
    : `/stage/${encodedId}/allocation-edit`
  const latestApprovalDate = approvals
    .map((row) => row?.approved_at || row?.rejected_at || row?.updated_at)
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0]
  const approvalDateText = latestApprovalDate ? formatDateShort(latestApprovalDate.toISOString()) : null
  const budgetId = budget.id
  const canUpdateBudget = Boolean(user?.permissions?.['contracts.update'])
  const normalizedBudgetStatus = String(previewBudgetStatus || '').toLowerCase().trim()
  const isUnderApproval = normalizedBudgetStatus === 'в согласовании'
  const canSendForApproval = Boolean(budgetId) && canUpdateBudget

  const refreshApprovals = async () => {
    await Promise.all([fetchReport(), notifications?.refreshPendingApprovals()])
  }

  const sendForApproval = async () => {
    if (!budgetId) return
    setApprovalSending(true)
    setApprovalMessage({ type: '', text: '' })
    try {
      await api.post(`/company/budgets/${budgetId}/approve`)
      await refreshApprovals()
      setApprovalMessage({ type: 'success', text: isUnderApproval ? 'Бюджет переотправлен на согласование' : 'Бюджет отправлен на согласование' })
    } catch (err) {
      setApprovalMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось отправить на согласование' })
    } finally {
      setApprovalSending(false)
    }
  }

  const submitSignerApprove = async () => {
    if (!budgetId) return
    setSignerLoading(true)
    setApprovalMessage({ type: '', text: '' })
    try {
      await api.patch(`/company/budgets/${budgetId}/approvals/me`, { status: 'approved' })
      await refreshApprovals()
      setApprovalMessage({ type: 'success', text: 'Бюджет согласован' })
    } catch (err) {
      setApprovalMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить' })
    } finally {
      setSignerLoading(false)
    }
  }

  const submitSignerReject = async () => {
    if (!budgetId) return
    setSignerLoading(true)
    setApprovalMessage({ type: '', text: '' })
    try {
      await api.patch(`/company/budgets/${budgetId}/approvals/me`, {
        status: 'rejected',
        comment: rejectComment.trim() || undefined,
      })
      setRejectModalOpen(false)
      setRejectComment('')
      await refreshApprovals()
      setApprovalMessage({ type: 'success', text: 'Отклонение сохранено' })
    } catch (err) {
      setApprovalMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить' })
    } finally {
      setSignerLoading(false)
    }
  }

  const downloadPdf = async () => {
    setDownloading(true)
    setError(null)
    try {
      const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat?.().resolvedOptions?.()?.timeZone
      const params = new URLSearchParams(buildReportParams())
      if (tz) params.set('tz', tz)
      const response = await api.get(`/company/stages/${encodedId}/report/pdf?${params.toString()}`, { responseType: 'blob' })
      const fallback = `budget-stage-${String(stageType || id || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}-${new Date().toISOString().slice(0, 10)}.pdf`
      triggerBlobDownload(response.data, filenameFromDisposition(response.headers?.['content-disposition'], fallback), 'application/pdf')
    } catch (err) {
      setError(err.response?.status === 403 ? 'Недостаточно прав' : 'Не удалось скачать PDF')
    } finally {
      setDownloading(false)
    }
  }

  const downloadCsv = async () => {
    setDownloading(true)
    setError(null)
    try {
      const response = await api.get(`/company/stages/${encodedId}/report/csv?${buildReportParams()}`, { responseType: 'blob' })
      const fallback = `budget-stage-${String(stageType || id || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`
      triggerBlobDownload(response.data, filenameFromDisposition(response.headers?.['content-disposition'], fallback), 'text/csv;charset=utf-8')
    } catch (err) {
      setError(err.response?.status === 403 ? 'Недостаточно прав' : 'Не удалось скачать CSV')
    } finally {
      setDownloading(false)
    }
  }

  if (loading && !reportData) {
    return <div className="stage-budget-page stage-budget-page--state">Загрузка...</div>
  }

  if (error || (!reportData && !loading)) {
    return (
      <div className="stage-budget-page stage-budget-page--state">
        <p>{error || 'Стадия не найдена'}</p>
        <Button type="button" variant="primary" onClick={() => navigate('/company/contracts')}>
          Назад
        </Button>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: 'Главная', to: '/' },
    { label: 'Договоры', to: '/company/contracts' },
    contractId && { label: contractNumber, to: `/contract/${contractId}` },
    { label: `${stageType} — Бюджет` },
  ].filter(Boolean)

  const actions = [
    {
      id: 'edit-budget',
      label: 'Редактировать бюджет',
      icon: <Icon name="edit" size={14} />,
      onSelect: () => navigate(editBudgetUrl),
    },
    {
      id: 'csv',
      label: downloading ? 'Формируем...' : 'Скачать CSV',
      icon: <Icon name="download" size={14} />,
      disabled: downloading,
      onSelect: downloadCsv,
    },
    {
      id: 'pdf',
      label: downloading ? 'Формируем...' : 'Скачать PDF',
      icon: <Icon name="download" size={14} />,
      disabled: downloading,
      onSelect: downloadPdf,
    },
    approvalLink && {
      id: 'approvals',
      label: 'Открыть лист согласования',
      icon: <Icon name="check" size={14} />,
      onSelect: () => navigate(approvalLink),
    },
    { id: 'divider-navigation', type: 'divider' },
    {
      id: 'stage-card',
      label: 'Открыть карточку стадии',
      icon: <Icon name="stage" size={14} />,
      onSelect: () => navigate(`/stage/${encodedId}`),
    },
  ]

  return (
    <div className="stage-budget-page">
      <Breadcrumb items={breadcrumbItems} className="stage-budget-breadcrumb" />

      <header className="stage-budget-header">
        <div className="stage-budget-heading">
          <Button
            type="button"
            variant="ghost"
            className="stage-budget-back"
            aria-label="Назад"
            onClick={() => navigate(backUrl)}
          >
            <Icon name="chevronLeft" size={18} />
          </Button>
          <h1 className="ds-page-title">Бюджет {stageType}</h1>
          {contract.status ? (
            <StatusBadge variant={getContractStatusVariant(contract.status)}>
              {contractStatusLabel}
            </StatusBadge>
          ) : null}
          <StatusBadge variant={budgetStatusVariant}>
            Бюджет: {budgetStatusLabel.toLowerCase()}
          </StatusBadge>
        </div>
        <ActionMenu label="Действия" variant="primary" items={actions} />
      </header>

      {error && <div className="stage-budget-message stage-budget-message--error" role="alert">{error}</div>}

      <div className="stage-budget-layout">
        <section className={`stage-budget-passport${passportCollapsed ? ' is-collapsed' : ''}`} aria-label="Паспорт бюджета">
          <button
            type="button"
            className="stage-budget-card-head stage-budget-collapse-head"
            onClick={() => setPassportCollapsed((value) => !value)}
            aria-expanded={!passportCollapsed}
          >
            <span className="stage-budget-chevron" aria-hidden>▾</span>
            <span className="stage-budget-section-title">Паспорт</span>
            <span className="stage-budget-passport__summary">
              {[stageType, objectName, `бюджет ${formatMoney(model.stageBudget)}`].filter(Boolean).join(' · ')}
            </span>
          </button>
          <div className="stage-budget-collapsible-body">
            {stage.correction_description && <div className="stage-budget-info-value" style={{padding:'16px 24px',whiteSpace:'pre-wrap'}}><strong>Что корректируем</strong><p>{stage.correction_description}</p></div>}
            <div className="stage-budget-info-grid">
              <div>
                <div className="stage-budget-info-label">Тип стадии</div>
                <div className="stage-budget-info-value">{stageType}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">Объект</div>
                <div className="stage-budget-info-value">
                  {contract.object_id ? <Link to={`/object/${contract.object_id}`}>{objectName || '—'}</Link> : objectName || '—'}
                </div>
              </div>
              <div>
                <div className="stage-budget-info-label">Клиент</div>
                <div className="stage-budget-info-value">
                  {contract.client_id ? <Link to={`/client/${contract.client_id}`}>{clientName || '—'}</Link> : clientName || '—'}
                </div>
              </div>
              <div>
                <div className="stage-budget-info-label">Договор</div>
                <div className="stage-budget-info-value">{contractNumber}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">Срок</div>
                <div className="stage-budget-info-value">{formatDateShort(plannedDate)}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">Статус</div>
                <div className="stage-budget-info-value">{contractStatusLabel || '—'}</div>
              </div>
            </div>
            <div className="stage-budget-info-divider" />
            <div className="stage-budget-kpi-grid">
              <div>
                <div className="stage-budget-info-label">Бюджет</div>
                <div className="stage-budget-kpi-value">{formatMoney(model.stageBudget)}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">Заактировано</div>
                <div className="stage-budget-kpi-value stage-budget-kpi-value--green">{formatMoney(model.stageActuatedGross)}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">За вычетом налога</div>
                <div className="stage-budget-kpi-value">{formatMoney(model.stageActuated)}</div>
              </div>
              <div>
                <div className="stage-budget-info-label">Остаток</div>
                <div className="stage-budget-kpi-value stage-budget-kpi-value--blue">{formatMoney(model.remaining)}</div>
              </div>
            </div>
          </div>
        </section>

        {!stage.correction_name && <section className={`stage-budget-approval-card stage-budget-approval-card--${budgetStatusVariant}${approvalCollapsed ? ' is-collapsed' : ''}`} aria-label="Согласование бюджета">
          <div
            role="button"
            tabIndex={0}
            className="stage-budget-approval-head stage-budget-collapse-head"
            onClick={() => setApprovalCollapsed((value) => !value)}
            onKeyDown={(event) => toggleOnEnterOrSpace(event, () => setApprovalCollapsed((value) => !value))}
            aria-expanded={!approvalCollapsed}
          >
            <div>
              <div className="stage-budget-approval-title">
                <span className="stage-budget-chevron" aria-hidden>▾</span>
                Бюджет {budgetStatusLabel.toLowerCase()}
              </div>
              <div className="stage-budget-approval-meta">
                {approvalDateText ? `${approvalDateText} · ` : ''}
                <span className="stage-budget-approval-meta__actions">
                  {approvalLink ? <Link to={approvalLink} onClick={(event) => event.stopPropagation()}>Открыть лист согласования</Link> : 'Лист согласования недоступен'}
                  <Button
                    type="button"
                    variant="ghost"
                    className="stage-budget-approval-print"
                    aria-label="Скачать подписанный бюджет PDF"
                    title="Скачать подписанный бюджет PDF"
                    disabled={downloading}
                    onClick={(event) => {
                      event.stopPropagation()
                      downloadPdf()
                    }}
                  >
                    <Icon name="print" size={16} aria-hidden />
                  </Button>
                </span>
              </div>
            </div>
            <div className="stage-budget-approval-progress">
              <div className="stage-budget-approval-progress__text">
                {approvalTotal > 0 ? (
                  <>Согласовали <strong>{approvedCount}</strong> из <strong>{approvalTotal}</strong></>
                ) : (
                  'Согласующие не назначены'
                )}
              </div>
              <div className="stage-budget-approval-progress__bar" aria-hidden>
                <span className="stage-budget-approval-progress__approved" style={{ width: `${approvalProgress}%` }} />
                {rejectedCount > 0 ? (
                  <span className="stage-budget-approval-progress__rejected" style={{ width: `${Math.round((rejectedCount / approvalTotal) * 100)}%` }} />
                ) : null}
                {approvalTotal > approvedCount + rejectedCount ? (
                  <span className="stage-budget-approval-progress__waiting" style={{ width: `${Math.max(100 - approvalProgress - (rejectedCount > 0 ? Math.round((rejectedCount / approvalTotal) * 100) : 0), 0)}%` }} />
                ) : null}
              </div>
            </div>
          </div>

          <div className="stage-budget-collapsible-body">
            {approvalTotal > 0 ? (
              <div className="stage-budget-signer-list">
                {approvals.map((approval, index) => {
                  const state = getApprovalState(approval)
                  return (
                    <div key={approval.id || `${approval.employee_name}-${index}`} className={`stage-budget-signer-row stage-budget-signer-row--${state}`}>
                      <div className="stage-budget-signer-avatar">{getInitials(approval.employee_name)}</div>
                      <div className="stage-budget-signer-info">
                        <div className="stage-budget-signer-name">{approval.employee_name || '—'}</div>
                        <div className="stage-budget-signer-role">{approval.position || approval.role || 'Согласующий'}</div>
                      </div>
                      <span className={`stage-budget-signer-status stage-budget-signer-status--${state}`}>
                        <Icon name={state === 'approved' ? 'check' : state === 'rejected' ? 'warning' : 'file-check'} size={12} aria-hidden />
                        {getApprovalStateLabel(approval)}
                      </span>
                      {getApprovalDate(approval) ? <span className="stage-budget-signer-date">{getApprovalDate(approval)}</span> : null}
                      {isUnderApproval && state === 'waiting' && isOwnApprovalRow(approval, user) ? (
                        <span className="stage-budget-signer-actions">
                          <Button
                            type="button"
                            variant="primary"
                            className="stage-budget-signer-action"
                            disabled={signerLoading}
                            onClick={submitSignerApprove}
                          >
                            <Icon name="check" size={13} aria-hidden /> Согласовать
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="stage-budget-signer-action"
                            disabled={signerLoading}
                            onClick={() => { setRejectComment(''); setRejectModalOpen(true) }}
                          >
                            <Icon name="close" size={13} aria-hidden /> Отклонить
                          </Button>
                        </span>
                      ) : null}
                      {approval.comment ? (
                        <div className="stage-budget-signer-comment">
                          <div>Комментарий</div>
                          {approval.comment}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="stage-budget-signer-empty">Согласующие по бюджету не заданы</div>
            )}

            {approvalMessage.text ? (
              <div
                className={`stage-budget-message${approvalMessage.type === 'error' ? ' stage-budget-message--error' : ' stage-budget-message--success'}`}
                role="alert"
              >
                {approvalMessage.text}
              </div>
            ) : null}

            <div className="stage-budget-approval-actions">
              <div className="stage-budget-approval-result-group">
                <span className={`stage-budget-approval-result stage-budget-approval-result--${budgetStatusVariant}`}>
                  <Icon name={budgetStatusVariant === 'success' ? 'check' : 'file-check'} size={15} aria-hidden />
                  {budgetStatusVariant === 'success'
                    ? 'Бюджет согласован — действий не требуется'
                    : budgetStatusVariant === 'review'
                      ? 'Бюджет находится на согласовании'
                      : 'Бюджет ещё не отправлен на согласование'}
                </span>
              </div>
              {canSendForApproval ? (
                <Button
                  type="button"
                  variant={isUnderApproval ? 'secondary' : 'primary'}
                  className="stage-budget-approval-send"
                  disabled={approvalSending}
                  onClick={sendForApproval}
                >
                  <Icon name={isUnderApproval ? 'refresh' : 'send'} size={15} aria-hidden />
                  {approvalSending
                    ? 'Отправка…'
                    : isUnderApproval
                      ? 'Переотправить на согласование'
                      : 'Отправить на согласование'}
                </Button>
              ) : null}
            </div>
          </div>
        </section>}

        <main className="stage-budget-main">
          <div className="stage-budget-period-bar">
            <label className="stage-budget-period-option">
              <input
                type="radio"
                name="stage-budget-period"
                checked={periodType === 'all'}
                onChange={() => { setPeriodType('all'); setSelectedQuarter(null) }}
              />
              <span>Всё время</span>
            </label>
            <label className="stage-budget-period-option">
              <input
                type="radio"
                name="stage-budget-period"
                checked={periodType === 'quarter'}
                onChange={() => setPeriodType('quarter')}
              />
              <span>Квартал</span>
            </label>
            {periodType === 'quarter' && (
              <Select
                value={selectedQuarter ? `${selectedQuarter.year}-${selectedQuarter.quarter}` : ''}
                onChange={(event) => {
                  const value = event.target.value
                  if (!value) {
                    setSelectedQuarter(null)
                    return
                  }
                  const [year, quarter] = value.split('-').map(Number)
                  setSelectedQuarter(quarters.find((item) => item.year === year && item.quarter === quarter) || null)
                }}
                className="stage-budget-quarter-select"
              >
                <option value="">Выберите квартал</option>
                {quarters.map((quarter) => (
                  <option key={`${quarter.year}-${quarter.quarter}`} value={`${quarter.year}-${quarter.quarter}`}>
                    {quarter.label}
                  </option>
                ))}
              </Select>
            )}
          </div>

          {periodType === 'quarter' && !selectedQuarter && quarters.length > 0 && (
            <div className="stage-budget-message">Выберите квартал для отчёта за период.</div>
          )}

          <div className="stage-budget-rules-bar" aria-label="Правила распределения бюджета">
            {primaryCustomRulesIssue && (
              <div className="stage-budget-custom-percentages-warning" role="status">
                <div>
                  <strong>Распределение не завершено</strong>
                  <span>
                    По команде {primaryCustomRulesIssue.team} задано {toNumber(primaryCustomRulesIssue.total_percentage).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}% вместо 100%. {customRulesIssueLabel}.
                  </span>
                </div>
                <Link to={customRulesUrl}>Задать уникальные проценты</Link>
              </div>
            )}
            {customRulesCount > 0 ? (
              <Link className="stage-budget-rule-chip stage-budget-rule-chip--custom" to={customRulesUrl} title="Открыть уникальные правила распределения">
                <Icon name="settings" size={15} aria-hidden />
                <span>Заданы <strong>уникальные правила распределения</strong></span>
                <span className="stage-budget-rule-chip__count">{customRulesCount}</span>
                <Icon name="chevronRight" size={14} className="stage-budget-rule-chip__arrow" aria-hidden />
              </Link>
            ) : (
              <Link className="stage-budget-rule-chip stage-budget-rule-chip--action" to={createCustomRulesUrl} title="Задать уникальные проценты распределения">
                <Icon name="settings" size={15} aria-hidden />
                <span>Задать уникальные проценты распределения</span>
                <Icon name="chevronRight" size={14} className="stage-budget-rule-chip__arrow" aria-hidden />
              </Link>
            )}

            {gipRulesCount > 0 ? (
              <Link className="stage-budget-rule-chip stage-budget-rule-chip--custom" to={gipRulesUrl} title="Открыть правила ГИПов">
                <Icon name="users" size={15} aria-hidden />
                <span>Заданы <strong>правила ГИПов</strong></span>
                <span className="stage-budget-rule-chip__count">{gipRulesCount}</span>
                <Icon name="chevronRight" size={14} className="stage-budget-rule-chip__arrow" aria-hidden />
              </Link>
            ) : (
              <Link className="stage-budget-rule-chip stage-budget-rule-chip--action" to={createGipRulesUrl} title="Задать правила ГИПов">
                <Icon name="users" size={15} aria-hidden />
                <span>Задать правила ГИПов</span>
                <Icon name="chevronRight" size={14} className="stage-budget-rule-chip__arrow" aria-hidden />
              </Link>
            )}
          </div>

          <StageBudgetTable
            reportData={reportData}
            selectedActId={selectedActId}
            onSelectedActIdChange={setSelectedActId}
            tableLabel="Бюджет стадии"
          />

        </main>
        <EntityComments entityType="budgets" entityId={budgetId} />
      </div>

      {rejectModalOpen && (
        <div className="modal-overlay" role="presentation" onClick={() => !signerLoading && setRejectModalOpen(false)}>
          <div className="modal-content" role="dialog" aria-labelledby="stage-budget-reject-title" onClick={(event) => event.stopPropagation()}>
            <h3 id="stage-budget-reject-title">Отклонить согласование</h3>
            <FormGroup label="Комментарий (причина отклонения)">
              <textarea
                className="ui-input"
                value={rejectComment}
                onChange={(event) => setRejectComment(event.target.value)}
                placeholder="Причина отклонения"
                rows={3}
              />
            </FormGroup>
            <div className="modal-actions">
              <Button variant="secondary" type="button" onClick={() => setRejectModalOpen(false)} disabled={signerLoading}>Отмена</Button>
              <Button variant="primary" type="button" onClick={submitSignerReject} disabled={signerLoading}>Сохранить</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StageReportPreview
